import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import { useCrudEditContext } from "@ryde/redux";
import {
  controlsSF,
  dynamicFormTabIndexAF,
  fieldNameMetadataResolve,
  getFormLabelKey,
  getIsShownByConditionRenderTemplate,
  ICustomField,
  IExtendableDto,
  INVISIBLE_FORM_TAB_LABEL,
  profilePermissionsSelector,
  removeMetadataPrefix,
  systemSettingsDtoAtom,
  tabSF,
} from "@ryde/shared";

import {
  normalizeSearchText,
  normalizeSearchTextFlat,
} from "./system-params.drawer-edit.utils";

export interface ISystemParamsTabViewModel {
  tab: { label?: string; fieldIds?: number[] };
  originalIndex: number;
  count: number;
  matchedControlIds: Set<number>;
}

export const useSystemParamsEdit = (): {
  searchValue: string;
  setSearchValue: (v: string) => void;
  isSearching: boolean;
  totalFound: number;
  tabIndex: number;
  uiTabIndex: number;
  handleTabChange: (_: React.SyntheticEvent, newTabIndex: number) => void;
  tabsNoInvisible: { label?: string; fieldIds?: number[] }[];
  visibleTabViewModels: ISystemParamsTabViewModel[];
  currentTabControls: ICustomField[];
} => {
  const { t } = useTranslation();
  const {
    entity,
    data: { dto },
  } = useCrudEditContext();
  const { getValues } = useFormContext();
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const roleDataItem = useRecoilValue(profilePermissionsSelector);

  const allTabs = useRecoilValue(tabSF(entity));
  const allControls = useRecoilValue(controlsSF(entity));
  const [tabIndex, setTabIndex] = useRecoilState(dynamicFormTabIndexAF(entity));
  const [uiTabIndex, setUiTabIndex] = useState(tabIndex);

  const [searchValue, setSearchValue] = useState("");
  const searchNormalized = useMemo(
    () => normalizeSearchText(searchValue),
    [searchValue],
  );
  const searchNormalizedFlat = useMemo(
    () => normalizeSearchTextFlat(searchValue),
    [searchValue],
  );
  const searchTokens = useMemo(
    () => searchNormalized.split(" ").filter(Boolean),
    [searchNormalized],
  );
  const isSearching = Boolean(searchTokens.length);

  const tabsNoInvisible = useMemo(() => {
    return (allTabs ?? []).filter(
      tab => tab.label !== INVISIBLE_FORM_TAB_LABEL,
    );
  }, [allTabs]);

  const controlsById = useMemo(() => {
    const map = new Map<number, ICustomField>();
    (allControls ?? []).forEach(c => {
      if (c?.id !== undefined) {
        map.set(Number(c.id), c);
      }
    });
    return map;
  }, [allControls]);

  const doesControlMatchSearch = useCallback(
    (control: ICustomField): boolean => {
      if (!isSearching) {
        return true;
      }

      const fieldNameResolved = fieldNameMetadataResolve(
        control.name,
        control.isColumn,
      );
      const paramKey = removeMetadataPrefix(fieldNameResolved);

      const i18nKey = getFormLabelKey(entity, fieldNameResolved);
      const i18nValue = t(i18nKey);
      const translatedLabel =
        control.labelTranslated ||
        (i18nValue !== i18nKey ? i18nValue : undefined) ||
        control.formLabel ||
        "";

      const haystack = normalizeSearchText(`${translatedLabel} ${paramKey}`);
      const haystackFlat = normalizeSearchTextFlat(
        `${translatedLabel} ${paramKey}`,
      );

      const tokensMatch = searchTokens.every(token => haystack.includes(token));
      const flatMatch =
        searchNormalizedFlat.length > 0 &&
        haystackFlat.includes(searchNormalizedFlat);

      return tokensMatch || flatMatch;
    },
    [entity, isSearching, searchNormalizedFlat, searchTokens, t],
  );

  const tabViewModels = useMemo((): ISystemParamsTabViewModel[] => {
    const formValues = getValues();

    return tabsNoInvisible.map((tab, originalIndex) => {
      const fieldIds = tab.fieldIds ?? [];

      const matchedControls = fieldIds
        .map(id => controlsById.get(Number(id)))
        .filter((c): c is ICustomField => Boolean(c?.formFieldTypeId))
        .filter(c => doesControlMatchSearch(c))
        .filter(c => {
          const isShownByCondition = getIsShownByConditionRenderTemplate({
            formValues,
            formVisibleCondition: c.formVisibleCondition,
            isCheckDtoValuesForVisibleConditions:
              c.formAttributes?.checkDtoValuesForVisibleConditions ?? false,
            dto: dto as IExtendableDto | undefined,
            isCheckSystemParamValuesForVisibleConditions:
              c.formAttributes?.checkSystemParamValuesForVisibleConditions ??
              false,
            systemParams: systemSettingsDto?.metadata ?? {},
            permissions: roleDataItem?.permissions,
          });
          return isShownByCondition;
        });

      return {
        tab,
        originalIndex,
        count: isSearching ? matchedControls.length : 0,
        matchedControlIds: new Set(matchedControls.map(c => Number(c.id))),
      };
    });
  }, [
    controlsById,
    doesControlMatchSearch,
    isSearching,
    tabsNoInvisible,
    getValues,
    dto,
    systemSettingsDto,
    roleDataItem,
  ]);

  const visibleTabViewModels = useMemo(() => {
    if (!isSearching) {
      return tabViewModels;
    }

    return tabViewModels.filter(vm => vm.count > 0);
  }, [isSearching, tabViewModels]);

  const totalFound = useMemo(() => {
    if (!isSearching) {
      return 0;
    }

    return visibleTabViewModels.reduce((acc, vm) => acc + vm.count, 0);
  }, [isSearching, visibleTabViewModels]);

  useEffect(() => {
    setUiTabIndex(tabIndex);
  }, [tabIndex]);

  useEffect(() => {
    if (!visibleTabViewModels.length) {
      if (tabIndex !== 0) {
        setTabIndex(0);
      }
      return;
    }

    const isActiveTabVisible = visibleTabViewModels.some(
      vm => vm.originalIndex === tabIndex,
    );

    if (!isActiveTabVisible) {
      setTabIndex(visibleTabViewModels[0].originalIndex);
    }
  }, [setTabIndex, tabIndex, visibleTabViewModels]);

  const handleTabChange = useEvent(
    (_: React.SyntheticEvent, newTabIndex: number): void => {
      const nextTabIndex = Number(newTabIndex);
      setUiTabIndex(nextTabIndex);
      startTransition(() => {
        setTabIndex(nextTabIndex);
      });
    },
  );

  const currentTabControls = useMemo(() => {
    const activeVm =
      visibleTabViewModels.find(vm => vm.originalIndex === tabIndex) ??
      visibleTabViewModels[0];

    const fieldIds = activeVm?.tab?.fieldIds ?? [];

    if (!fieldIds.length) {
      return [];
    }

    const matchedIds = activeVm?.matchedControlIds;
    const formValues = getValues();

    return fieldIds
      .map(id => controlsById.get(Number(id)))
      .filter((c): c is ICustomField => Boolean(c?.formFieldTypeId))
      .filter(c =>
        !isSearching ? true : Boolean(matchedIds?.has(Number(c.id))),
      )
      .filter(c => {
        const isShownByCondition = getIsShownByConditionRenderTemplate({
          formValues,
          formVisibleCondition: c.formVisibleCondition,
          isCheckDtoValuesForVisibleConditions:
            c.formAttributes?.checkDtoValuesForVisibleConditions ?? false,
          dto: dto as IExtendableDto | undefined,
          isCheckSystemParamValuesForVisibleConditions:
            c.formAttributes?.checkSystemParamValuesForVisibleConditions ??
            false,
          systemParams: systemSettingsDto?.metadata ?? {},
          permissions: roleDataItem?.permissions,
        });
        return isShownByCondition;
      });
  }, [
    controlsById,
    isSearching,
    tabIndex,
    visibleTabViewModels,
    getValues,
    dto,
    systemSettingsDto,
    roleDataItem,
  ]);

  return {
    searchValue,
    setSearchValue,
    isSearching,
    totalFound,
    tabIndex,
    uiTabIndex,
    handleTabChange,
    tabsNoInvisible,
    visibleTabViewModels,
    currentTabControls,
  };
};
